import { service } from '@ember/service';
import Route from '@ember/routing/route';
import type PersonModel from 'tio-data/models/person';
import type SessionContextService from 'tio-employee/services/session-context';
import type StoreService from 'ember-data/store';
import type EmployeeService from 'tio-employee/services/employee';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type { ReimaginedContributionHistoryModel } from 'tio-employee/components/reimagined/production/contribution-history';
import type FeaturesService from 'tio-common/services/features';
import type SlrLoanStandingVerificationService from 'tio-employee/services/slr-loan-standing-verification';
import type { SlrLoanStandingAuditState } from 'tio-employee/types/slr-loan-standing-verification';
import { SLR_LOAN_STANDING_FEATURE_FLAG } from 'tio-employee/types/slr-loan-standing-verification';

export default class AuthenticatedSlrDashboard extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare employee: EmployeeService;
  @service declare reimaginedData: ReimaginedDataService;
  @service declare features: FeaturesService;
  @service('slr-loan-standing-verification')
  declare slrLoanStandingVerification: SlrLoanStandingVerificationService;

  private loadLoanStandingVerification = async (personId: string) => {
    if (this.features.isDisabled(SLR_LOAN_STANDING_FEATURE_FLAG)) return null;

    try {
      return await this.slrLoanStandingVerification.fetchCurrentState(personId);
    } catch (error) {
      console.warn('[SLR loan standing] Failed to load current audit state', error);
      return null;
    }
  };

  async model() {
    // This is a hack to make sure that contributions data is loaded after an doc upload for DEV-4807
    // TODO: Employee contributions data is a good candidate for updating to request manager to improve this flow
    if (this.employee.contributionsState.isFinished) {
      this.employee.contributionsState.retry();
    }
    if (this.employee.futurePayments.isFinished) {
      this.employee.futurePayments.retry();
    }
    const personId = this.sessionContext.user.person.id;
    if (!personId) {
      throw new Error('Cannot load SLR dashboard without a person id.');
    }

    const [
      person,
      contributionHistory,
      employerContributions,
      section127Info,
      loanStandingVerification,
    ] = await Promise.all([
      this.store.findRecord<PersonModel>('person', personId, {
        include: 'accounts.financial-institution,accounts.loans,accounts.postal-address',
        reload: true,
      }),
      this.reimaginedData.fetchContributionHistory(),
      this.reimaginedData.fetchEmployerContributions(),
      this.reimaginedData.fetchSection127Info(),
      this.loadLoanStandingVerification(personId),
    ]);

    const employee = this.sessionContext.currentEmployee;
    const hasStrategyFinderResults = (employee?.strategyFinderResults?.length ?? 0) > 0;

    const reimagined: ReimaginedContributionHistoryModel = {
      contributionHistory,
      employerContributions,
      person,
      section127Info,
      hasStrategyFinderResults,
    };

    return { person, reimagined, loanStandingVerification };
  }
}

export interface AuthenticatedSlrDashboardModel {
  person: PersonModel;
  reimagined: ReimaginedContributionHistoryModel;
  loanStandingVerification: SlrLoanStandingAuditState | null;
}
